import React, { useState, useEffect } from 'react';
import { THEME } from '../../utils/theme';
import api from '../../utils/api';
import Spinner from '../ui/Spinner';

const TopTenModal = ({ onClose, onShowDetail }) => {
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userTab, setUserTab] = useState(null); // null = combined, username for specific user
  const [users, setUsers] = useState([]);

  // Load users from stats API on mount
  useEffect(() => {
    api.get('/api/stats').then(data => {
      if (data.user_stats) {
        setUsers(Object.entries(data.user_stats).map(([username, userData]) => ({
          username,
          displayName: userData.display_name,
          color: userData.color || THEME.success,
          icon: userData.icon
        })));
      }
    }).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const endpoint = userTab ? `/api/games/top-30-days?user=${userTab}` : '/api/games/top-30-days';
    api.get(endpoint).then(data => {
      setGames(data.games || []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [userTab]);

  const maxPlaytime = games.length > 0 ? Math.max(...games.map(g => g.recent_playtime_minutes || 0)) : 0;

  // Build tabs dynamically from users
  const tabs = [
    { value: null, label: 'Combined', color: `linear-gradient(135deg, ${THEME.omega}, ${THEME.supe})` },
    ...users.map(u => ({ value: u.username, label: `${u.icon || ''} ${u.displayName}`, color: u.color }))
  ];

  const currentTabColor = userTab ? (users.find(u => u.username === userTab)?.color || THEME.success) : `linear-gradient(135deg, ${THEME.omega}, ${THEME.supe})`;

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(5,5,10,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, backdropFilter: 'blur(20px) saturate(180%)', WebkitBackdropFilter: 'blur(20px) saturate(180%)' }} onClick={onClose}>
      <div style={{ background: `linear-gradient(145deg, ${THEME.bgElevated}, ${THEME.bgCard})`, borderRadius: '20px', padding: '24px', width: '95%', maxWidth: '500px', maxHeight: '85vh', overflow: 'auto', border: `1px solid ${THEME.border}`, boxShadow: `0 25px 80px ${THEME.shadowHeavy}`, animation: 'scaleIn 0.2s ease' }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h2 style={{ margin: 0, color: THEME.text, fontSize: '20px', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span style={{ fontSize: '24px' }}>🔥</span> Top 10 (Last 30 Days)
          </h2>
          <button onClick={onClose} style={{ background: THEME.bgHover, border: 'none', borderRadius: '8px', padding: '8px 12px', color: THEME.textMuted, cursor: 'pointer' }}>✕</button>
        </div>

        {/* User Tabs */}
        <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
          {tabs.map(tab => (
            <button
              key={tab.value || 'combined'}
              onClick={() => setUserTab(tab.value)}
              className="press-effect"
              style={{
                flex: 1,
                background: userTab === tab.value ? (typeof tab.color === 'string' && tab.color.startsWith('linear') ? tab.color : tab.color) : THEME.bg,
                color: userTab === tab.value ? '#000' : (tab.value ? tab.color : THEME.textMuted),
                border: `1px solid ${userTab === tab.value ? 'transparent' : THEME.border}`,
                borderRadius: '10px',
                padding: '10px 12px',
                fontSize: '13px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.15s'
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}><Spinner /></div>
        ) : games.length === 0 ? (
          <div style={{ textAlign: 'center', color: THEME.textMuted, padding: '40px' }}>No sessions in the last 30 days</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {games.map((game, idx) => (
              <div key={game.id} onClick={() => { onShowDetail(game); onClose(); }} style={{ padding: '12px', background: THEME.bg, borderRadius: '12px', cursor: 'pointer', border: `1px solid ${THEME.border}` }}>
                <div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginBottom: '8px' }}>
                  <div style={{ width: '28px', height: '28px', background: idx < 3 ? `linear-gradient(135deg, ${idx === 0 ? '#fbbf24' : idx === 1 ? '#94a3b8' : '#cd7f32'}, ${idx === 0 ? '#f59e0b' : idx === 1 ? '#64748b' : '#a0522d'})` : THEME.bgElevated, borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '700', color: idx < 3 ? '#000' : THEME.textMuted, fontSize: '12px' }}>{idx + 1}</div>
                  <img src={game.header_image} alt="" style={{ width: '70px', height: '40px', objectFit: 'cover', borderRadius: '6px' }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ color: THEME.text, fontWeight: '600', fontSize: '13px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{game.name}</div>
                    <div style={{ color: THEME.textMuted, fontSize: '11px' }}>{game.recent_session_count || 0} sessions</div>
                  </div>
                  <div style={{ color: currentTabColor.startsWith('linear') ? THEME.success : currentTabColor, fontWeight: '700', fontSize: '14px' }}>
                    {Math.round((game.recent_playtime_minutes || 0) / 60)}h
                  </div>
                </div>
                <div style={{ height: '6px', background: THEME.bg, borderRadius: '3px', overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${maxPlaytime > 0 ? ((game.recent_playtime_minutes || 0) / maxPlaytime) * 100 : 0}%`, background: currentTabColor, borderRadius: '3px', transition: 'width 0.5s ease' }} />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default TopTenModal;
